import React, { useEffect, useRef, useContext } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import StarekContext from "../../context/StarekContext";
import {
    SidebarView,
    SidebarContext,
    SidebarContextProvider,
    useSyncedField,
    useSidebarField,
} from "../../components/Sidebar";

function ZoneBasicDetails({ parentOptions }) {
    const { entity } = useContext(SidebarContext);

    const [zoneName, setZoneName] = useSyncedField("name", "");
    const [zoneDesc, setZoneDesc] = useSyncedField("description", "");
    const [parentOpt, setParentOpt] = useSyncedField("parent", null);

    const parentOptionsWithNone = [
        { value: null, label: "--None--" },
        ...parentOptions,
    ];

    const entityIdRef = useRef(null);

    useEffect(() => {
        const currentEntityId = entity?.id || null;
        if (
            entityIdRef.current === currentEntityId &&
            currentEntityId !== null
        ) {
            return;
        }
        entityIdRef.current = currentEntityId;

        if (entity) {
            setZoneName(entity.name || "");
            setZoneDesc(entity.description || "");

            // Find parent zone option
            const currentParentId = entity.parent?.id ?? entity.parent_id;
            const found = currentParentId
                ? parentOptions.find(
                      (o) => Number(o.value) === Number(currentParentId),
                  )
                : { value: null, label: "--None--" };
            setParentOpt(found || { value: null, label: "--None--" });
        } else {
            // Reset for new zone
            setZoneName("");
            setZoneDesc("");
            setParentOpt(
                parentOptions.find((o) => Number(o.value) === 1) || null,
            );
        }
    }, [entity?.id, parentOptions]);

    return (
        <Form
            className="d-flex flex-column h-100"
            onSubmit={(e) => e.preventDefault()}
        >
            <Form.Group className="mb-3">
                <Form.Label>Parent Zone</Form.Label>
                <Select
                    inputId="parent_zones"
                    options={parentOptionsWithNone}
                    value={parentOpt}
                    onChange={(opt) => setParentOpt(opt)}
                    placeholder="Select a zone"
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>
                    Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Enter here"
                    value={zoneName}
                    onChange={(e) => setZoneName(e.target.value)}
                    required
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Max allowed characters: 120"
                    value={zoneDesc}
                    onChange={(e) => setZoneDesc(e.target.value)}
                />
            </Form.Group>
        </Form>
    );
}

function ZoneDevices({ deviceOptions }) {
    const { entity } = useContext(SidebarContext);
    const [selectedDevices, setSelectedDevices] = useSyncedField("devices", []);

    const [initialDeviceIds, setInitialDeviceIds] = useSidebarField(
        "initialDeviceIds",
        new Set(),
    );

    const entityIdRef = useRef(null);

    useEffect(() => {
        const currentEntityId = entity?.id || null;

        if (
            entityIdRef.current === currentEntityId &&
            currentEntityId !== null
        ) {
            return;
        }
        entityIdRef.current = currentEntityId;

        if (entity) {
            if (entity.installed_devices) {
                const byId = new Map(
                    (deviceOptions || []).map((o) => [String(o.value), o]),
                );
                const deviceIds = new Set(
                    entity.installed_devices.map((d) => String(d.id)),
                );
                setInitialDeviceIds(deviceIds);

                const prefilled = entity.installed_devices.map((d) => {
                    const hit = byId.get(String(d.id));
                    return hit || { value: d.id, label: d.name || d.id };
                });
                setSelectedDevices(prefilled);
            } else {
                setInitialDeviceIds(new Set());
                setSelectedDevices([]);
            }
        } else {
            setSelectedDevices([]);
            setInitialDeviceIds(new Set());
        }
    }, [entity?.id, deviceOptions]);

    const devicesArray = Array.isArray(selectedDevices) ? selectedDevices : [];

    const handleUnlink = (deviceValue) => {
        const filtered = devicesArray.filter((x) => x.value !== deviceValue);
        setSelectedDevices(filtered);
    };

    return (
        <Form className="d-flex flex-column h-100">
            <Form.Group className="mb-3">
                <Form.Label>
                    Devices <span className="text-danger">*</span>
                </Form.Label>
                <Select
                    options={deviceOptions}
                    placeholder="Select devices"
                    isMulti
                    value={devicesArray}
                    onChange={(val) => setSelectedDevices(val || [])}
                    components={{ MultiValue: () => null }}
                    closeMenuOnSelect={true}
                    hideSelectedOptions={true}
                />
            </Form.Group>

            {devicesArray.length > 0 && (
                <>
                    <div className="mb-2 fw-semibold">Linked Devices</div>
                    <div className="d-flex flex-column" style={{ gap: 10 }}>
                        {devicesArray.map((d) => {
                            const isNew = !initialDeviceIds.has(
                                String(d.value),
                            );
                            return (
                                <div
                                    key={d.value}
                                    className="d-flex align-items-center justify-content-between border rounded px-3 py-2"
                                >
                                    <div
                                        className="d-flex align-items-center"
                                        style={{ gap: 10 }}
                                    >
                                        <span className="badge bg-light text-dark">
                                            <i
                                                className="fa fa-bolt"
                                                aria-hidden="true"
                                            />
                                        </span>

                                        {isNew && (
                                            <span className="badge bg-success text-uppercase">
                                                NEW
                                            </span>
                                        )}

                                        <span className="text-truncate">
                                            {d.label}
                                        </span>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-link text-danger p-0"
                                        onClick={() => handleUnlink(d.value)}
                                        title="Unlink"
                                        aria-label={`Unlink ${d.label}`}
                                    >
                                        <i
                                            className="fa fa-unlink"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </Form>
    );
}

const ZoneSidebar = (props) => {
    const {
        show,
        onDiscard,
        item = null,
        plantId,
        parentOptions = [],
        deviceOptions = [],
        onSave = async () => {},
        onDelete = async () => {},
        onToggleActive,
    } = props;
    const starekContext = useContext(StarekContext);

    const handleSaveInternal = async (emittedData, entity) => {
        try {
            if (!emittedData.name?.trim()) {
                starekContext.showError("Zone name is required");
                throw new Error("Zone name is required");
            }
            if (!plantId) {
                starekContext.showError("Plant ID is required");
                throw new Error("Plant ID is required");
            }

            let parentPayload = {};
            if (emittedData.parent !== undefined) {
                if (emittedData.parent?.value === null) {
                    parentPayload = { parent_id: null };
                } else if (emittedData.parent?.value != null) {
                    parentPayload = {
                        parent_id: Number(emittedData.parent.value),
                    };
                }
            }

            const payload = {
                id: entity?.id,
                name: emittedData.name.trim(),
                description: emittedData.description || "",
                plant_id: Number(plantId),
                ...parentPayload,
                is_active: true,
                selected_device_ids: (emittedData.devices || []).map(
                    (d) => d?.value,
                ),
            };

            await onSave(payload);

            starekContext.showSuccess(
                entity?.id
                    ? `Zone "${payload.name}" updated successfully`
                    : `Zone "${payload.name}" created successfully`,
            );
        } catch (error) {
            console.error("Error saving zone:", error);
            starekContext.showError(
                error?.message || "Failed to save zone. Please try again.",
            );
            throw error;
        }
    };

    return (
        <SidebarContextProvider
            entity={item}
            show={show}
            onSave={handleSaveInternal}
            onDelete={onDelete}
            onDiscard={onDiscard}
            onToggleActive={onToggleActive}
            initialPage="basic_details"
        >
            <SidebarView entityName="Zone">
                <SidebarView.Page name="basic_details" title="Basic Details">
                    <ZoneBasicDetails parentOptions={parentOptions} />
                </SidebarView.Page>

                <SidebarView.Page name="devices" title="Devices">
                    <ZoneDevices deviceOptions={deviceOptions} />
                </SidebarView.Page>
            </SidebarView>
        </SidebarContextProvider>
    );
};

export default ZoneSidebar;
